import { PaymentType } from "@1games-ru/region-shared/dist/enums/payment/payment";
import { motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import formatPrice from "@/core/helpers/formatPrice";
import { handleNumericChange } from "@/core/helpers/handleNumericChange";
import { BankCardIcon, CashIcon, Plus2Icon, RubleIcon } from "@/shared/icons";
import { Button, TextInput } from "@/shared/ui";
import CloseButton from "@/shared/ui/CloseButton";
import useTopUpBusinessBalance from "@/domains/business/hooks/useTopUpBusinessBalance";

type Props = {
  businessId: number;
  onClose: () => void;
};

export default function BusinessUpBalanceContainer({ businessId, onClose }: Props) {
  const { t } = useTranslation("business");

  const [inputValue, setInputValue] = useState("");
  const [payType, setPayType] = useState<PaymentType>(PaymentType.Card);

  const { isTopUpBalanceLoading, topUpBalance } = useTopUpBusinessBalance(businessId);

  const handleInputChange = (value: string) => handleNumericChange(value, setInputValue);

  const onTopUpBalance = useCallback(() => {
    topUpBalance(Number(inputValue), payType, () => {
      setInputValue("");
      onClose();
    });
  }, [inputValue, onClose, payType, topUpBalance]);

  useEffect(() => {
    return () => setPayType(PaymentType.Card);
  }, []);

  return (
    <div className="absolute z-3 top-0 left-0 size-full">
      <motion.div
        className="size-full bg-dark-900/84 rounded-tl-4 rounded-tr-4 overflow-clip"
        style={{ backdropFilter: "blur(2px)" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        onClick={onClose}
      />
      <motion.div
        className="absolute left-0 bottom-0 w-full min-h-52 p-7.5 bg-dark-900 rounded-tl-4 rounded-tr-4 shadow-[0_0_200px_0_rgba(1,100,172,0.6)]"
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        transition={{
          type: "spring",
          damping: 20,
          stiffness: 150,
        }}
      >
        <div className="pl-1 flex justify-between items-center">
          <p className="text-18 font-bold leading-none">{t("business.topUpBalance")}</p>
          <CloseButton onClose={onClose} />
        </div>

        <div className="my-5 bg-[#191919]">
          <div className="w-[11.6875rem] h-px bg-accent-solid" />
        </div>

        <div className="flex items-end gap-3.5">
          <div>
            <p className="mb-3.5 text-16 font-medium">{t("business.enterAmount")}:</p>
            <TextInput
              className="w-65"
              inputClassName="h-11"
              value={inputValue}
              onChange={handleInputChange}
              placeholder={t("business.safe.enterAmount")}
              icon={<RubleIcon className="size-5.5 text-accent-solid" />}
              isIconRight
              maxLength={11}
            />
          </div>

          <div>
            <p className="mb-3.5 text-16 font-medium">{t("business.choosePaymentType")}:</p>
            <div className="grid grid-cols-2">
              <Button
                variant={payType === PaymentType.Card ? "solid-surface" : "soft"}
                color={payType === PaymentType.Card ? "accent" : "gray"}
                className="flex items-center gap-2 w-[10.625rem] h-11 px-4 py-3.5 font-bold"
                onClick={() => setPayType(PaymentType.Card)}
              >
                <BankCardIcon className="size-4.5" />
                {t("business.purchaseType.card")}
              </Button>
              <Button
                variant={payType === PaymentType.Cash ? "solid-surface" : "soft"}
                color={payType === PaymentType.Cash ? "accent" : "gray"}
                className="flex items-center gap-2 w-[10.625rem] h-11 px-4 py-3.5 font-bold"
                onClick={() => setPayType(PaymentType.Cash)}
              >
                <CashIcon className="size-4.5" />
                {t("business.purchaseType.cash")}
              </Button>
            </div>
          </div>

          <div className="flex justify-end items-center gap-5 ml-auto">
            <div>
              <p className="text-18 font-bold text-accent-solid text-right">
                {formatPrice(Number(inputValue))} ₽
              </p>
              <p className="text-15 font-medium text-white/50 text-right">
                {t("business.totalToPay")}
              </p>
            </div>
            <Button
              color="accent"
              className="flex items-center gap-2 w-50 h-11"
              disabled={isTopUpBalanceLoading || !inputValue}
              loading={isTopUpBalanceLoading}
              onClick={onTopUpBalance}
            >
              <Plus2Icon className="size-4.5" />
              {t("business.up")}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
